import { useState } from 'react'
import { Modal } from './modal'
import { StarRating } from './star-rating'
import { useCreateReflection } from '@/hooks/use-reflections'
import type { ActionPlanReflection } from '@/types'

interface ReflectionFormProps {
  isOpen: boolean
  onClose: () => void
  planId: string
  existingReflection?: ActionPlanReflection
  // Archive flow: show skip option + call onArchive after submit/skip
  archiveMode?: boolean
  onArchive?: () => void
}

export function ReflectionForm({ isOpen, onClose, planId, existingReflection, archiveMode, onArchive }: ReflectionFormProps) {
  const [whatWorked, setWhatWorked] = useState(existingReflection?.what_worked || '')
  const [whatDidntWork, setWhatDidntWork] = useState(existingReflection?.what_didnt_work || '')
  const [generalThoughts, setGeneralThoughts] = useState(existingReflection?.general_thoughts || '')
  const [rating, setRating] = useState(existingReflection?.effectiveness_rating || 0)
  const createReflection = useCreateReflection()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (rating < 1) return

    await createReflection.mutateAsync({
      planId,
      what_worked: whatWorked || undefined,
      what_didnt_work: whatDidntWork || undefined,
      general_thoughts: generalThoughts || undefined,
      effectiveness_rating: rating,
    })

    if (archiveMode && onArchive) onArchive()
    onClose()
  }

  const handleSkipArchive = () => {
    if (onArchive) onArchive()
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={archiveMode ? 'Add Reflection Before Archiving' : existingReflection ? 'Edit Reflection' : 'Add Reflection'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Effectiveness Rating */}
        <div>
          <label className="block text-sm font-medium text-cfa-dark-gray mb-1">
            Effectiveness Rating <span className="text-danger-500">*</span>
          </label>
          <StarRating value={rating} onChange={setRating} />
          {rating === 0 && <p className="text-xs text-cfa-dark-gray/60 mt-1">Click a star to rate</p>}
        </div>

        {/* What Worked */}
        <div>
          <label className="block text-sm font-medium text-cfa-dark-gray mb-1">What worked well?</label>
          <textarea
            value={whatWorked}
            onChange={(e) => setWhatWorked(e.target.value.slice(0, 1000))}
            className="w-full rounded-lg border border-cfa-gray px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
            rows={3}
            placeholder="Strategies, processes, or decisions that led to success..."
          />
          <p className="text-xs text-cfa-dark-gray/50 text-right">{whatWorked.length}/1000</p>
        </div>

        {/* What Didn't Work */}
        <div>
          <label className="block text-sm font-medium text-cfa-dark-gray mb-1">What didn't work?</label>
          <textarea
            value={whatDidntWork}
            onChange={(e) => setWhatDidntWork(e.target.value.slice(0, 1000))}
            className="w-full rounded-lg border border-cfa-gray px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
            rows={3}
            placeholder="Obstacles, mistakes, or things to avoid next time..."
          />
          <p className="text-xs text-cfa-dark-gray/50 text-right">{whatDidntWork.length}/1000</p>
        </div>

        {/* General Thoughts */}
        <div>
          <label className="block text-sm font-medium text-cfa-dark-gray mb-1">General thoughts</label>
          <textarea
            value={generalThoughts}
            onChange={(e) => setGeneralThoughts(e.target.value.slice(0, 1000))}
            className="w-full rounded-lg border border-cfa-gray px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
            rows={3}
            placeholder="Any other observations or takeaways..."
          />
          <p className="text-xs text-cfa-dark-gray/50 text-right">{generalThoughts.length}/1000</p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-2 border-t border-cfa-light-gray">
          <div>
            {archiveMode && (
              <button type="button" onClick={handleSkipArchive} className="text-sm text-cfa-dark-gray/60 hover:text-cfa-dark-gray">
                Archive without reflection
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-cfa-dark-gray/70 hover:text-cfa-dark-gray">
              Cancel
            </button>
            <button
              type="submit"
              disabled={rating === 0 || createReflection.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue/90 rounded-lg disabled:opacity-50 transition-colors"
            >
              {createReflection.isPending ? 'Saving...' : archiveMode ? 'Save & Archive' : 'Save Reflection'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
